import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Container } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Header, MuiBox, MuiGrid, MuiIconButton, SidebarNew } from "..";
import { useGetUserPreferredLanguage } from "../../api/services/user/userApi";
import { Context } from "../../common/context/Context";
import { LANGUAGE_CODE } from "../../helpers/constants";
import { isNotEmpty } from "../../helpers/utils";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import {
  fullSidebarSelector,
  sidebarSelector,
} from "../../redux/slices/appState/appStateSelector";
import {
  setUserPreferredLanguage,
  toggleFullSidebar,
  toggleLoader,
  toggleSideBar,
  toggleToast,
} from "../../redux/slices/appState/appStateSlice";
import { Footer } from "../footer/Footer";
import { AppLayoutWrapper, ConditionalGrid } from "./AppLayout.style";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";

interface AppLayoutProps {
  children: ReactNode;
  handlePreferredLanguageChange: (language: string) => void;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  handlePreferredLanguageChange,
}) => {
  const context = useContext(Context);
  const dispatch = useDispatch();
  const { commonARIALabel } = useNavigationSiteCore();

  const scrollRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const [isLanguageSelected, setIsLanguageSelected] = useState(false);
  const {
    data: languageData,
    isPending: isPendingLanguage,
    isError: isErrorLanguage,
  } = useGetUserPreferredLanguage();

  useEffect(() => {
    if (scrollRef.current) {
      // scrolling to top of the page when route change
      scrollRef.current.scrollTop = 0;
    }
  }, [location]);

  /**for sidebar state */
  const { isOpen } = useSelector(sidebarSelector);
  const { isOpen: isFullSidebarOpen, showCollapseButton } =
    useSelector(fullSidebarSelector);
  /**if user is inactive close sidebar */
  useEffect(() => {
    if (!context?.isUserActive) {
      dispatch(toggleSideBar({ isOpen: false }));
    }
  }, [context?.isUserActive]);

  const { isSmallScreen, isRealDesktop } = useMediaQueries();

  useEffect(() => {
    if (isSmallScreen) {
      dispatch(toggleSideBar({ isOpen: false }));
    } else {
      dispatch(toggleSideBar({ isOpen: true }));
    }
  }, [isSmallScreen]);

  //Get user selected language and store in redux
  useEffect(() => {
    if (isNotEmpty(languageData)) {
      setIsLanguageSelected(true);
      dispatch(setUserPreferredLanguage(languageData?.preferredLanguage));
      handlePreferredLanguageChange(languageData?.preferredLanguage);
    }
    if (isErrorLanguage) {
      setIsLanguageSelected(true);
      dispatch(setUserPreferredLanguage(LANGUAGE_CODE.EN_US));
      handlePreferredLanguageChange(LANGUAGE_CODE.EN_US);
      dispatch(
        toggleToast({
          open: true,
          message: "Oops, something went wrong...!",
          type: "error",
        }),
      );
    }
  }, [languageData, isErrorLanguage]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isPendingLanguage,
        message: "Loading... Please wait...",
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isPendingLanguage]);

  const renderCollapseIcon = () => {
    return (
      <MuiIconButton
        className="side-bar-collapse-icon"
        onClick={() => dispatch(toggleFullSidebar(!isFullSidebarOpen))}
        aria-label={
          isFullSidebarOpen
            ? commonARIALabel.collapseLeftNavigation
            : commonARIALabel.expandLeftNavigation
        }
      >
        {isFullSidebarOpen ? (
          <ArrowBackIosIcon className="left-arrow" />
        ) : (
          <ArrowForwardIosIcon className="right-arrow" />
        )}
      </MuiIconButton>
    );
  };

  const renderSideBar = () => {
    const sidebarGridSize = isSmallScreen
      ? { md: 3, xs: 12 }
      : { sm: isFullSidebarOpen ? 3 : 0, xs: 12 };

    return (
      <ConditionalGrid
        size={sidebarGridSize}
        className={`side-bar-main ${
          isRealDesktop ? "desktop-sidebar" : ""
          //only in desktop mode, other wise it will be applied on mobile as well
        }`}
        isVisible={isSmallScreen ? isOpen : true} // keep it mounted on desktop
      >
        <aside id="main-navigation">
          {!isSmallScreen && showCollapseButton && renderCollapseIcon()}

          {/** Only render the sidebar content when needed */}
          {(isSmallScreen ? isOpen : isFullSidebarOpen) && <SidebarNew />}
        </aside>
      </ConditionalGrid>
    );
  };

  const renderContent = () => {
    const contentGridSize = isSmallScreen
      ? { sm: 12, md: 9, xs: 12 }
      : { sm: isFullSidebarOpen ? 9 : 12, xs: 12 };
    return (
      <ConditionalGrid
        size={contentGridSize}
        isVisible={isSmallScreen ? !isOpen : true}
        className={
          isRealDesktop && isFullSidebarOpen ? "desktop-right-content" : ""
        }
      >
        <main>
          <MuiBox
            className="content-container"
            ref={scrollRef}
            id="main-content"
          >
            <Container className="app-layout-container">{children}</Container>
          </MuiBox>
        </main>
      </ConditionalGrid>
    );
  };

  const renderLayoutGrid = () => {
    return (
      <>
        {renderSideBar()}
        {renderContent()}
      </>
    );
  };

  return (
    <AppLayoutWrapper>
      {isLanguageSelected && (
        <>
          <MuiBox>
            <a href="#main-content" className="skip-to-content" tabIndex={0}>
              Skip to main content
            </a>
            <a href="#main-navigation" className="skip-to-content" tabIndex={0}>
              Skip to main navigation
            </a>
            <CssBaseline />
            <Header />
          </MuiBox>
          <MuiGrid container className="grid-container">
            {renderLayoutGrid()}
          </MuiGrid>
          <Footer />
        </>
      )}
    </AppLayoutWrapper>
  );
};

export default AppLayout;
